export type SegmentTest = (segment: string) => boolean;

// `master|bachelor|phd` style patterns: a plain list of literal alternatives.
const PLAIN_ALTERNATION_RE = /^[\w-]+(?:\|[\w-]+)*$/;

// Same as vue-router's default param pattern `[^/]+?` for a single path segment.
const acceptAnySegment: SegmentTest = (segment) => segment.length > 0;

/**
 * Compiles each route param pattern (e.g. all disciplines for `:di`) once, on first use, and shares the
 * result between all route templates and resolvers for the lifetime of the process.
 * Literal alternatives become a Set lookup; anything else becomes one anchored, case-insensitive RegExp.
 */
export class ParamPatternRegistry {
	private readonly compiled = new Map<string, SegmentTest>();

	constructor(private readonly patterns: Readonly<Record<string, string | undefined>>) {}

	public get(key: string): SegmentTest {
		let test = this.compiled.get(key);
		if (!test) {
			test = this.compile(this.patterns[key]);
			this.compiled.set(key, test);
		}

		return test;
	}

	private compile(pattern: string | undefined): SegmentTest {
		if (!pattern) return acceptAnySegment;

		if (PLAIN_ALTERNATION_RE.test(pattern)) {
			const values = new Set(pattern.toLowerCase().split('|'));
			return (segment) => values.has(segment.toLowerCase());
		}

		const regex = new RegExp(`^(?:${pattern})$`, 'i');
		return (segment) => regex.test(segment);
	}
}
